import { subscribersAPI } from "../../services/api";
import { useState, useRef } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { FaInstagram, FaFacebookF, FaArrowRight, FaEnvelope, FaMapMarkerAlt } from "react-icons/fa";
import CircularBadge from "../badge";
import ForgeLogo from "../logo";
import "./Footer.css";
import CerroStamp from "../utils/CerroStamp";

const ease = [0.22, 1, 0.36, 1];
const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.65, ease, delay } },
});

/* ── Nav columns ─────────────────────────────────────────── */
const NAV_COLS = [
  {
    heading: "Explore",
    links: [
      { label: "About Forge", href: "/#about" },
      { label: "The Retreat", href: "/#retreat" },
      { label: "Research", href: "/#research" },
      { label: "Apply / Register", href: "/apply" },
      { label: "Veterans", href: "/#who-we-serve" },
      { label: "First Responders", href: "/#who-we-serve" },
      { label: "Gold Star Families", href: "/#who-we-serve" },
    ],
  },
  {
    heading: "Resources",
    links: [
      { label: "Retreat Preparation", href: "/#retreat" },
      { label: "FAQs", href: "/#about" },
      { label: "Community Stories", href: "/community" },
      { label: "Research & Science", href: "/#research" },
      { label: "Events", href: "/community" },
      { label: "Contact Us", href: "/contact" },
    ],
  },
  {
    heading: "Get Involved",
    links: [
      { label: "Donate", href: "/donate" },
      { label: "Sponsor a Retreat", href: "/donate" },
      { label: "Partner With Us", href: "/contact" },
      { label: "Volunteer", href: "/contact" },
      { label: "Spread the Word", href: "/community" },
    ],
  },
];

/* ── Social links ────────────────────────────────────────── */
const SOCIALS = [
  { icon: FaInstagram, href: "https://www.instagram.com/forgengo", label: "Instagram" },
  { icon: FaFacebookF, href: "https://www.facebook.com/groups/1713906612814894/", label: "Facebook" },
  // { icon: FaYoutube, href: "https://www.youtube.com/@forgeretreat", label: "YouTube" },
  // { icon: FaLinkedinIn, href: "https://www.linkedin.com/company/forgeretreat", label: "LinkedIn" },
];

/* ── Coordinate stamp ────────────────────────────────────── */

/* ── Mountain + forest SVG illustration ─────────────────── */
function MountainScene() {
  const photoRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: photoRef,
    offset: ["start end", "end start"],
  });
  const photoY = useTransform(scrollYProgress, [0, 1], ["20%", "-30%"]);

  return (
    <div className="ftr-new__mountain" aria-hidden="true" ref={photoRef}>
      <motion.img src="./assets/mountainsForst.png" alt="" className="ftr-new__mountain-img" style={{ y: photoY }} />
    </div>
  );
}

/* ── Main footer ─────────────────────────────────────────── */
export default function Footer() {
  const [email, setEmail] = useState("");
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await subscribersAPI.subscribe(email, "Footer");
    } catch (_) {}
    setEmail("");
  };

  const flowerRef = useRef(null);

  const { scrollYProgress: flowerScroll } = useScroll({
    target: flowerRef,
    offset: ["start end", "end start"],
  });

  const flowerY = useTransform(flowerScroll, [0, 1], ["0%", "-28%"]);

  return (
    <footer className="ftr-new" id="contact" ref={ref}>
      {/* ══════════════════════════════════════════
          TOP SECTION — logo + tagline + nav cols + newsletter
         ══════════════════════════════════════════ */}
      <div className="ftr-new__top container-fg container-2xxl">
        {/* Col 1 — Brand + tagline */}
        <motion.div className="ftr-new__brand-col" variants={fadeUp(0)} initial="hidden" animate={inView ? "show" : "hidden"}>
          {/* Logo lockup */}
          <div className="ftr-new__logo-lockup">
            <ForgeLogo fill="var(--c-forest)" width={42} height={42} />
            <span className="ftr-new__wordmark">FORGE</span>
          </div>

          {/* Rust rule */}
          <span className="ftr-new__brand-rule" aria-hidden="true" />

          <p className="ftr-new__tagline">A transformational retreat for veterans, first responders, and their families.</p>
          <p className="ftr-new__tagline">Seven days in nature to heal what the world never asked you to carry.</p>

          {/* Botanical leaf illustration */}
          <div className="ftr-new__leaf" ref={flowerRef} aria-hidden="true">
            <motion.img src="./assets/plant.png" alt="" className="ftr-new__leaf-img" style={{ y: flowerY }} />
          </div>
        </motion.div>

        {/* Nav columns */}
        {NAV_COLS.map((col, ci) => (
          <motion.div key={col.heading} className="ftr-new__nav-col" variants={fadeUp(0.06 + ci * 0.06)} initial="hidden" animate={inView ? "show" : "hidden"}>
            <h3 className="ftr-new__col-heading">{col.heading}</h3>
            <span className="ftr-new__col-rule" aria-hidden="true" />
            <ul className="ftr-new__col-links">
              {col.links.map((l) => (
                <li key={l.label}>
                  <a href={l.href} className="ftr-new__link">
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>
        ))}

        {/* Stay connected col */}
        <motion.div className="ftr-new__connect-col" variants={fadeUp(0.24)} initial="hidden" animate={inView ? "show" : "hidden"}>
          <h3 className="ftr-new__col-heading">Stay Connected</h3>
          <span className="ftr-new__col-rule" aria-hidden="true" />

          <p className="ftr-new__connect-sub">Get updates on upcoming retreats, stories, and ways to get involved.</p>

          {/* Email form */}
          <form className="ftr-new__form" onSubmit={handleSubmit}>
            <label htmlFor="ftr-email" className="visually-hidden">
              Email address
            </label>
            <div className="ftr-new__form-row">
              <input id="ftr-email" type="email" inputMode="email" placeholder="Enter your email" value={email} onChange={(e) => setEmail(e.target.value)} className="ftr-new__input" required />
              <button type="submit" className="ftr-new__submit" aria-label="Subscribe">
                <FaArrowRight />
              </button>
            </div>
          </form>

          {/* Follow us */}
          <div className="ftr-new__social">
            <h4 className="ftr-new__social-heading">Follow Us</h4>
            <span className="ftr-new__col-rule" aria-hidden="true" />
            <div className="ftr-new__social-icons">
              {SOCIALS.map(({ icon: Icon, href, label }) => (
                <a key={label} href={href} className="ftr-new__social-link" aria-label={label}>
                  <Icon />
                </a>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* ══════════════════════════════════════════
          MIDDLE — mountain illustration + stamp
         ══════════════════════════════════════════ */}
      <div className="ftr-new__mid">
        <MountainScene />
        <motion.div className="ftr-new__stamp-wrap" variants={fadeUp(0.18)} initial="hidden" animate={inView ? "show" : "hidden"}>
          <CerroStamp baseColor={"var(--c-sand)"} bColor="var(--c-rust)" bSize={3} />
        </motion.div>
      </div>

      {/* ══════════════════════════════════════════
          BOTTOM BAR — dark band
         ══════════════════════════════════════════ */}
      <div className="ftr-new__bar">
        <div className="ftr-new__bar-inner container-fg container-2xxl">
          {/* Badge */}
          <div className="ftr-new__bar-badge">
            <CircularBadge colors="var(--c-sand)" />
          </div>

          {/* Location block */}
          <div className="ftr-new__bar-block">
            <p className="ftr-new__bar-eyebrow">Retreat Location</p>
            <p className="ftr-new__bar-location u-display">Colombia</p>
            <p className="ftr-new__bar-location-sub">
              In the heart of nature.
              <br />
              Away from the noise.
            </p>
          </div>

          {/* Contact block */}
          <div className="ftr-new__bar-block">
            <p className="ftr-new__bar-eyebrow">Contact</p>
            <a href="mailto:info@forge.ngo" className="ftr-new__bar-contact">
              <FaEnvelope /> info@forge.ngo
            </a>
            <a href="/contact" className="ftr-new__bar-contact">
              <FaMapMarkerAlt /> Colombia
            </a>
          </div>

          {/* Legal block */}
          <div className="ftr-new__bar-block">
            <p className="ftr-new__bar-eyebrow">Legal</p>
            {["Privacy Policy", "Terms of Use", "Refund Policy"].map((l) => (
              <a key={l} href={`/${l.toLowerCase().replace(/\s/g, "-")}`} className="ftr-new__bar-legal-link">
                {l}
              </a>
            ))}
          </div>

          {/* Copyright + tagline */}
          <div className="ftr-new__bar-copy">
            <p>
              © {new Date().getFullYear()} Forge NGO
              <br />
              All rights reserved.
            </p>
            <p className="ftr-new__bar-made">
              MADE BY BROTHERS.
              <br />
              BUILT FOR BROTHERS.
            </p>
          </div>
        </div>

        {/* ── 501(c)(3) disclosure — full-width strip below the column grid ── */}
        <div className="ftr-new__tax-strip container-fg container-2xxl">
          <p className="ftr-new__tax-main">FORGE NGO is a registered 501(c)(3) public charity.</p>
          <span className="ftr-new__tax-divider" aria-hidden="true" />
          <p className="ftr-new__tax-detail">
            EIN: <strong>33-2406117</strong>
            <span className="ftr-new__tax-mid" aria-hidden="true">
              ·
            </span>
            Effective: Dec 16, 2024
            <span className="ftr-new__tax-mid" aria-hidden="true">
              ·
            </span>
            Contributions are tax-deductible under IRC Section 170.
          </p>
          <a href="/documents/forge-tax-exemption.pdf" target="_blank" rel="noreferrer noopener" className="ftr-new__tax-link" aria-label="View IRS exemption letter PDF">
            View exemption letter ↗
          </a>
        </div>
      </div>
    </footer>
  );
}
